import React, { createContext, useContext, useState, useEffect } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * Resolve the sites a user can access.
 * Returns:
 *  - null  → unrestricted (admin with no allowed_sites, or legacy site='ALL')
 *  - []    → no access (non-admin with explicit empty allowed_sites)
 *  - [...] → restricted to these site codes (applies to admins too if they
 *            picked specific sites — admin CRUD rights are unaffected)
 */
export function getUserAllowedSites(user) {
  if (!user) return [];
  const allowed = user.allowed_sites;
  if (Array.isArray(allowed)) {
    if (allowed.length === 0) {
      const legacy = user.site || "ALL";
      if (legacy === "ALL") {
        return user.role === "admin" ? null : [];
      }
      return [legacy];
    }
    return allowed.filter((s) => typeof s === "string" && s);
  }
  const legacy = user.site || "ALL";
  if (legacy === "ALL") return user.role === "admin" ? null : [];
  return [legacy];
}

/** Filter a full sites list to only those visible for a user. */
export function filterSitesForUser(sitesList, user) {
  const scope = getUserAllowedSites(user);
  if (scope === null) return sitesList;
  return sitesList.filter((s) => scope.includes(s.code));
}

const TOKEN_KEY = "flotteops_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = not authed
  const [loading, setLoading] = useState(true);

  // Initialize from localStorage and bootstrap axios header
  useEffect(() => {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (!tok) {
      setUser(false);
      setLoading(false);
      return;
    }
    api.defaults.headers.common["Authorization"] = `Bearer ${tok}`;
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        delete api.defaults.headers.common["Authorization"];
        setUser(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    api.defaults.headers.common["Authorization"] = `Bearer ${r.data.token}`;
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common["Authorization"];
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function formatApiError(detail) {
  if (detail == null) return "Une erreur s'est produite.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
